import { HttpErrorResponse } from '@angular/common/http';
import {
  Injectable,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { isNumber } from 'lodash-es';
import {
  Observable,
  of,
} from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import {
  WebCommonStorageRepository,
} from 'webcommon/legacy-common';
import {
  LoginDto,
  UserLoginInfoDto,
} from 'webcommon/shared';
import {
  LoginRepository,
} from 'webcommon/webapi';

const loginErrorCodes = {
  UnknownError: 1,
  InvalidRequest: 2,
  UnknownClientId: 4,
  LoginFailed: 8,
  NotMobileUser: 16,
  InactiveUser: 32,
  NotAllowedToLogin: 64,
  PrmVersionUnsupported: 128,
  MultipleUserAccounts: 256,
  CouldNotValidateUser: 512,
};

export interface PrmLoginSuccessfulResult {
  success: true;
  userLoginInfo: UserLoginInfoDto;
}

export interface PrmLoginMultipleUserResult {
  success: false;
  hasMultipleUsers: true;
  userLoginInfo: UserLoginInfoDto;
}

export interface PrmLoginFailureResult {
  success: false;
  hasMultipleUsers: false;
  message: string;
  userLoginInfo?: UserLoginInfoDto;
}

export type PrmLoginResult = PrmLoginSuccessfulResult | PrmLoginMultipleUserResult | PrmLoginFailureResult;

@Injectable({
  providedIn: 'root',
})
export class PrmLoginService {
  static readonly ajsFactoryName = 'PrmLoginServiceTs';

  constructor(
    private loginRepository: LoginRepository,
    private translateService: TranslateService,
    private webCommonStorageRepository: WebCommonStorageRepository,
  ) {
  }

  login(username: string, password: string, userId: string | null, clientFeatures: string[]): Observable<PrmLoginResult> {
    const clientId = this.webCommonStorageRepository.getClientId();
    const deviceId = this.webCommonStorageRepository.getDeviceId();

    if (!clientId) {
      throw new Error('clientId must be set in WebCommonStorageRepository');
    }

    const loginDto: LoginDto = {
      UserName: username,
      Password: password,
      ClientId: clientId,
      DeviceId: deviceId,
      UserId : userId,
      ClientFeatures: clientFeatures,
    };

    return this.loginRepository.prmLogin(loginDto).pipe(
      map(userLoginInfoDto => {
        const successResult: PrmLoginSuccessfulResult = {
          success: true,
          userLoginInfo: userLoginInfoDto,
        };
        return successResult;
      }),
      catchError((response: unknown) => {
        let msg = 'login error';
        let userLoginInfo: UserLoginInfoDto | undefined;

        if (response instanceof HttpErrorResponse && response.status === 401) {
          msg = 'Invalid username or password';
          userLoginInfo = response.error;

          if (userLoginInfo && isNumber(userLoginInfo.ErrorCode)) {
            /* eslint-disable no-bitwise */
            const errorCode = userLoginInfo.ErrorCode;
            if (errorCode & loginErrorCodes.MultipleUserAccounts) {
              const result: PrmLoginMultipleUserResult = {
                success: false,
                hasMultipleUsers: true,
                userLoginInfo,
              };
              return of(result);
            }

            if (errorCode & loginErrorCodes.UnknownClientId) {
              msg = 'Unknown clientId (' + clientId + ').';
            } else if (errorCode & loginErrorCodes.CouldNotValidateUser) {
              msg = 'Could not validate user.';
            } else if (errorCode & loginErrorCodes.LoginFailed) {
              msg = 'Invalid username or password';
            } else if (errorCode & loginErrorCodes.InactiveUser) {
              msg = 'This user account is not active and cannot login.';
            } else if (errorCode & loginErrorCodes.PrmVersionUnsupported) {
              msg = 'PRM version out of date.  Please contact customer support about upgrading.';
            }
            /* eslint-enable no-bitwise */
          }
        }

        return this.translateService.get(msg).pipe(
          map(message => {
            const failureResult: PrmLoginFailureResult = {
              success: false,
              hasMultipleUsers: false,
              userLoginInfo,
              message,
            };
            return failureResult;
          }),
        );
      }),
    );
  }
}
